/**
 * Where each level's open card sits, and where each level's own middle is.
 *
 * Measured rather than computed. The chart used to derive this from a card width
 * and a gap kept in step with the stylesheet by hand — which worked only while
 * every level was one unwrapped line of identical cards. Once levels wrap, the
 * answer depends on how many fit, which is the browser's to know, not ours.
 * Measuring also deleted the constant that had to match the CSS.
 *
 * One hook for ALL levels rather than one call per level: a chart's depth is
 * data, not source code, and calling a hook in a loop over a variable-length
 * array is exactly what React's rules forbid. So the refs live in one array and
 * a single effect measures them together.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface LevelMetrics {
  /** Centre of the open card, in its level's own coordinates. */
  cardCentre: number;
  /** Centre of the level itself — where a connector into it should land. */
  centre: number;
  /** Centres of the first and last cards: the ends of the bus. */
  firstCentre: number;
  lastCentre: number;
}

export interface Anchors {
  /** Attach to each level's element, by index. */
  levelRef: (index: number) => (el: HTMLDivElement | null) => void;
  metrics: LevelMetrics[];
  /** Re-measure now, for a caller that moves something itself. */
  measure: () => void;
}

const EMPTY: LevelMetrics = { cardCentre: 0, centre: 0, firstCentre: 0, lastCentre: 0 };

const centreOf = (card: Element, originLeft: number): number => {
  const box = card.getBoundingClientRect();
  return box.left - originLeft + box.width / 2;
};

/**
 * @param openKeys one entry per level: the `data-anchor` of its open card, or
 *                 null. Its length is the chart's depth.
 */
export function useAnchors(openKeys: (string | null)[]): Anchors {
  const levels = useRef<(HTMLDivElement | null)[]>([]);
  const [metrics, setMetrics] = useState<LevelMetrics[]>([]);

  // Keys compare by value: the array identity changes on every render, and
  // depending on it would re-measure forever.
  const signature = openKeys.join('\u0000');

  const measure = useCallback(() => {
    const keys = signature.split('\u0000');
    const next = keys.map((key, i) => {
      const level = levels.current[i];
      if (!level) return EMPTY;
      const box = level.getBoundingClientRect();
      const centre = box.width / 2;
      const cards = level.querySelectorAll('[data-anchor]');
      if (!cards.length) return { ...EMPTY, centre, cardCentre: centre };

      const firstCentre = centreOf(cards[0], box.left);
      const lastCentre = centreOf(cards[cards.length - 1], box.left);
      const open = key && level.querySelector(`[data-anchor="${CSS.escape(key)}"]`);
      return {
        // With nothing open the connector still has to land somewhere sensible,
        // so it falls back to the middle of the line.
        cardCentre: open ? centreOf(open, box.left) : centre,
        centre,
        firstCentre,
        lastCentre,
      };
    });
    // Only on a real change: a fresh array every time would make the layout
    // effect that calls this loop.
    setMetrics((cur) => (
      cur.length === next.length
      && cur.every((m, i) => m.cardCentre === next[i].cardCentre
        && m.centre === next[i].centre
        && m.firstCentre === next[i].firstCentre
        && m.lastCentre === next[i].lastCentre)
        ? cur
        : next
    ));
  }, [signature]);

  useLayoutEffect(measure, [measure]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    // A level that wraps changes height AND the position of everything in it, and
    // that happens without a resize event — a sibling panel opening is enough.
    const observer = new ResizeObserver(() => measure());
    levels.current.forEach((el) => el && observer.observe(el));
    return () => { window.removeEventListener('resize', measure); observer.disconnect(); };
  }, [measure]);

  const levelRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => { levels.current[index] = el; },
    [],
  );

  return { levelRef, metrics, measure };
}
